/* ------------------------------------------------------------------
   EmailSettings.js — Marketing → Email Marketing → Settings
   Sender identity with two modes:
     • shared  — generated authenticated address on the platform domain
                 (default; everyone).
     • domain  — the user's OWN verified domain (Pro/Agency): enter domain →
                 add 3 DKIM CNAMEs → verify → pick a From address on it.
   From name + reply-to apply in both modes.
------------------------------------------------------------------ */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const ax = axios.create({ withCredentials: true });
const isEmail = s => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || ''));

const STATUS_BADGE = {
  verified: { label: 'Verified',  bg: '#dcfce7', color: '#166534' },
  pending:  { label: 'Pending',   bg: '#fef9c3', color: '#854d0e' },
  failed:   { label: 'Failed',    bg: '#fef2f2', color: '#dc2626' },
  none:     { label: 'Not set up', bg: '#f3f4f6', color: '#6b7280' },
};

export default function EmailSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState(null);

  // shared / common
  const [mode, setMode]       = useState('shared');
  const [fromName, setFromName] = useState('');
  const [replyTo, setReplyTo]   = useState('');
  const [accountEmail, setAccountEmail] = useState('');
  const [sendingAddress, setSendingAddress] = useState('');
  const [canCustomDomain, setCanCustomDomain] = useState(false);

  // domain mode
  const [customDomain, setCustomDomain] = useState('');
  const [domainStatus, setDomainStatus] = useState('none');
  const [dkimRecords, setDkimRecords]   = useState([]);
  const [fromEmail, setFromEmail]       = useState('');
  const [domainInput, setDomainInput]   = useState('');
  const [localPart, setLocalPart]       = useState('hello');
  const [checking, setChecking]         = useState(false);

  const flash = (text, ok = true) => { setMsg({ text, ok }); setTimeout(() => setMsg(null), 5000); };

  const hydrate = useCallback((s) => {
    setMode(s.senderMode || 'shared');
    setFromName(s.fromName || '');
    setReplyTo(s.replyTo || '');
    setAccountEmail(s.accountEmail || '');
    setSendingAddress(s.sendingAddress || '');
    setCanCustomDomain(!!s.canCustomDomain);
    setCustomDomain(s.customDomain || '');
    setDomainStatus(s.customDomain ? (s.fromEmailStatus || 'pending') : 'none');
    setDkimRecords(s.dkimRecords || []);
    setFromEmail(s.fromEmail || '');
    if (s.fromEmail) setLocalPart(s.fromEmail.split('@')[0] || 'hello');
  }, []);

  useEffect(() => {
    (async () => {
      try { const { data } = await ax.get('/api/email/settings'); hydrate(data.settings || {}); }
      catch (e) { console.error('load settings', e); }
      finally { setLoading(false); }
    })();
  }, [hydrate]);

  async function saveDefaults(e) {
    e?.preventDefault();
    if (!fromName.trim()) { flash('Enter a from name.', false); return; }
    if (replyTo && !isEmail(replyTo)) { flash('Reply-to must be a valid email.', false); return; }
    setSaving(true);
    try {
      const { data } = await ax.put('/api/email/settings', { fromName: fromName.trim(), replyTo: replyTo.trim() });
      if (data.settings?.sendingAddress) setSendingAddress(data.settings.sendingAddress);
      flash('Settings saved.');
    } catch (err) { flash(err.response?.data?.error || 'Save failed', false); }
    finally { setSaving(false); }
  }

  async function startDomain() {
    const d = domainInput.trim().toLowerCase()
      .replace(/^[^@]*@/, '').replace(/^https?:\/\//, '').replace(/\/.*$/, '');  // tolerate email/URL paste
    if (!d) { flash('Enter your domain.', false); return; }
    setSaving(true);
    try {
      const { data } = await ax.post('/api/email/sender/domain', { domain: d });
      setCustomDomain(data.customDomain); setDomainStatus(data.status); setDkimRecords(data.records || []);
      setFromEmail(data.fromEmail || ''); setMode('domain');
      flash('Domain added. Now add the DNS records below, then click “Check verification”.');
    } catch (err) { flash(err.response?.data?.error || 'Could not start domain verification', false); }
    finally { setSaving(false); }
  }

  async function checkDomain() {
    setChecking(true);
    try {
      const { data } = await ax.get('/api/email/sender/domain/status');
      setDomainStatus(data.status); setDkimRecords(data.records || []);
      if (data.status === 'verified') flash('Domain verified — you can now send from it. 🎉');
      else if (data.status === 'pending') flash('Still pending — DNS can take a few minutes to propagate. Check again shortly.', false);
      else flash('Not verified yet.', false);
    } catch (err) { flash(err.response?.data?.error || 'Could not check status', false); }
    finally { setChecking(false); }
  }

  async function saveFromAddress() {
    const email = `${localPart.trim()}@${customDomain}`;
    if (!isEmail(email)) { flash('Enter a valid mailbox name.', false); return; }
    setSaving(true);
    try { await ax.put('/api/email/sender/from', { fromEmail: email }); setFromEmail(email); flash('From address saved.'); }
    catch (err) { flash(err.response?.data?.error || 'Could not save address', false); }
    finally { setSaving(false); }
  }

  async function useDomain() {
    setMode('domain');
    // if a verified domain already exists, commit domain mode server-side too
    if (customDomain && domainStatus === 'verified') {
      try { await ax.post('/api/email/sender/use-domain'); } catch { /* non-fatal */ }
    }
  }

  async function useShared() {
    setSaving(true);
    try { const { data } = await ax.post('/api/email/sender/shared'); setMode('shared'); if (data.sendingAddress) setSendingAddress(data.sendingAddress); flash('Switched to your Greatly Social address.'); }
    catch (err) { flash(err.response?.data?.error || 'Failed', false); }
    finally { setSaving(false); }
  }

  if (loading) return <div style={{ padding: 40, color: '#9ca3af' }}>Loading…</div>;
  const sb = STATUS_BADGE[domainStatus] || STATUS_BADGE.none;

  return (
    <div style={{ maxWidth: 660 }}>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>Settings</h1>
        <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>How your campaigns appear in recipients’ inboxes.</p>
      </div>

      {msg && <Banner msg={msg} />}

      {/* From name + reply-to (both modes) */}
      <form onSubmit={saveDefaults}>
        <Card title="Sender details">
          <Label>From name <Req/></Label>
          <input value={fromName} onChange={e => setFromName(e.target.value)} placeholder="e.g. Your Business Name" style={inputStyle} />
          <div style={hint}>The name recipients see in their inbox.</div>

          <Label style={{ marginTop: 18 }}>Reply-to email <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></Label>
          <input value={replyTo} onChange={e => setReplyTo(e.target.value)} placeholder={accountEmail || 'you@yourbusiness.com'} style={inputStyle} />
          <div style={hint}>Where replies go. Defaults to your account email{accountEmail ? ` (${accountEmail})` : ''}.</div>

          <div style={{ marginTop: 16 }}>
            <button type="submit" disabled={saving} style={{ ...primaryBtn, opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </Card>
      </form>

      {/* Sending address — mode switch */}
      <Card title="Sending address" style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <ModeTab active={mode === 'shared'} onClick={useShared} disabled={saving}>Greatly Social address</ModeTab>
          <ModeTab active={mode === 'domain'} onClick={useDomain} disabled={saving}>Your own domain {!canCustomDomain && <Lock/>}</ModeTab>
        </div>

        {mode === 'shared' ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <input value={sendingAddress || '—'} readOnly style={{ ...inputStyle, flex: 1, minWidth: 240, background: '#f9fafb', color: '#374151' }} />
              <Badge {...STATUS_BADGE.verified} label="Authenticated" />
            </div>
            <div style={hint}>Campaigns send from this authenticated address on our deliverability-managed domain — reliable inbox placement, zero setup.</div>
          </>
        ) : !canCustomDomain ? (
          <div style={{ padding: '16px 18px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10, fontSize: 14, color: '#374151' }}>
            Sending from your own domain is a <strong>Pro &amp; Agency</strong> feature. <span style={{ color: '#2563eb', fontWeight: 600 }}>Upgrade</span> to use it, or stick with your Greatly Social address (it works great).
          </div>
        ) : (
          <DomainFlow
            customDomain={customDomain} status={domainStatus} statusBadge={sb} records={dkimRecords}
            domainInput={domainInput} setDomainInput={setDomainInput} onStart={startDomain}
            onCheck={checkDomain} checking={checking} saving={saving}
            localPart={localPart} setLocalPart={setLocalPart} fromEmail={fromEmail} onSaveFrom={saveFromAddress}
            flash={flash}
          />
        )}
      </Card>
    </div>
  );
}

/* ── custom-domain sub-flow ── */
function DomainFlow({ customDomain, status, statusBadge, records, domainInput, setDomainInput, onStart, onCheck, checking, saving, localPart, setLocalPart, fromEmail, onSaveFrom, flash }) {
  const copy = (txt) => { navigator.clipboard?.writeText(txt); flash('Copied.'); };

  if (!customDomain) {
    return (
      <div>
        <Label>Your sending domain</Label>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <input value={domainInput} onChange={e => setDomainInput(e.target.value)} placeholder="yourbusiness.com" style={{ ...inputStyle, flex: 1, minWidth: 220 }} />
          <button onClick={onStart} disabled={saving} style={{ ...primaryBtn, whiteSpace: 'nowrap', opacity: saving ? 0.6 : 1 }}>{saving ? 'Starting…' : 'Add domain'}</button>
        </div>
        <div style={hint}>You’ll add 3 DNS records to prove ownership, then send from any address on it (e.g. hello@yourbusiness.com).</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span style={{ fontWeight: 700, color: '#111827' }}>{customDomain}</span>
        <Badge {...statusBadge} />
      </div>

      {status !== 'verified' && (
        <>
          <div style={{ fontSize: 13.5, color: '#374151', marginBottom: 10 }}>
            Add these <strong>3 CNAME records</strong> at your DNS provider, then click <strong>Check verification</strong>. DNS changes can take a few minutes (sometimes longer).
          </div>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 12 }}>
            {records.map((r, i) => (
              <div key={i} style={{ padding: 12, borderTop: i ? '1px solid #f1f5f9' : 'none' }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', color: '#9ca3af', marginBottom: 8 }}>CNAME RECORD {i + 1}</div>
                <CopyField label="Name / Host" value={r.name} onCopy={copy} />
                <CopyField label="Value / Points to" value={r.value} onCopy={copy} />
              </div>
            ))}
            {!records.length && <div style={{ padding: 14, color: '#9ca3af', fontSize: 13 }}>Loading records…</div>}
          </div>
          <div style={{ fontSize: 13, marginBottom: 12 }}>
            <a href="/email-domain-setup" target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}>📖 Step-by-step guide for GoDaddy, Namecheap, Cloudflare &amp; more →</a>
            <div style={{ color: '#9ca3af', marginTop: 6 }}>Tip: if your DNS host auto-appends your domain, enter the Name without the trailing <code>.{customDomain}</code>.</div>
          </div>
          <button onClick={onCheck} disabled={checking} style={{ ...primaryBtn, opacity: checking ? 0.6 : 1 }}>{checking ? 'Checking…' : 'Check verification'}</button>
        </>
      )}

      {status === 'verified' && (
        <div>
          <Label>Send from</Label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <input value={localPart} onChange={e => setLocalPart(e.target.value.replace(/[^a-z0-9._-]/gi, ''))} style={{ ...inputStyle, width: 160 }} />
            <span style={{ color: '#6b7280', fontWeight: 600 }}>@{customDomain}</span>
            <button onClick={onSaveFrom} disabled={saving} style={{ ...primaryBtn, opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
          <div style={hint}>{fromEmail ? <>Currently sending from <strong>{fromEmail}</strong>.</> : 'Pick the mailbox your campaigns send from.'}</div>
        </div>
      )}
    </div>
  );
}

/* ── bits ── */
const ModeTab = ({ active, onClick, disabled, children }) => (
  <button type="button" onClick={onClick} disabled={disabled} style={{
    flex: 1, padding: '9px 12px', borderRadius: 9, fontSize: 13.5, fontWeight: 600, cursor: disabled ? 'default' : 'pointer',
    border: `1px solid ${active ? '#2563eb' : '#d1d5db'}`, background: active ? '#eff6ff' : '#fff', color: active ? '#2563eb' : '#374151',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  }}>{children}</button>
);
const Lock = () => <i className="fas fa-lock" style={{ fontSize: 10, opacity: 0.7 }} />;
const CopyField = ({ label, value, onCopy }) => (
  <div style={{ marginBottom: 8 }}>
    <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 3 }}>{label}</div>
    <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
      <code style={{ flex: 1, minWidth: 0, fontFamily: 'monospace', fontSize: 12.5, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: '7px 10px', overflowWrap: 'anywhere', color: '#111827' }}>{value}</code>
      <button type="button" onClick={() => onCopy(value)} style={miniBtn}>Copy</button>
    </div>
  </div>
);
const Badge = ({ label, bg, color }) => <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 99, background: bg, color, whiteSpace: 'nowrap' }}>{label}</span>;
const inputStyle = { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, color: '#111827', outline: 'none', boxSizing: 'border-box' };
const primaryBtn = { padding: '10px 18px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' };
const miniBtn    = { padding: '5px 10px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' };
const hint = { fontSize: 12, color: '#9ca3af', marginTop: 6 };
const Req = () => <span style={{ color: '#dc2626' }}>*</span>;
const Label = ({ children, style }) => <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, ...style }}>{children}</div>;
const Card = ({ title, children, style }) => (
  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', ...style }}>
    <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 16 }}>{title}</div>
    {children}
  </div>
);
const Banner = ({ msg }) => (
  <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 8, fontSize: 14, fontWeight: 600,
    background: msg.ok ? '#ecfdf5' : '#fef2f2', color: msg.ok ? '#059669' : '#dc2626',
    border: `1px solid ${msg.ok ? '#a7f3d0' : '#fecaca'}` }}>{msg.text}</div>
);
